import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, getActiveClubName, getActiveClubLogo } from "../lib/api";
import { Printer, ArrowLeft, Target } from "lucide-react";

export default function MatchReport() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.matchReport(id).then(setData);
  }, [id]);

  if (!data) return <div className="font-display text-2xl p-8">Lade Bericht...</div>;
  const { match, player1, player2, season, totals } = data;
  const clubLogo = getActiveClubLogo();
  const winner = match.winner_id === player1.id ? player1 : player2;

  return (
    <>
      <style>{`
        @media print {
          html, body, #root, .App, .App > *, .App > * > * { height: auto !important; min-height: 0 !important; }
          body { background: white !important; color: black !important; }
          body::before { display: none !important; }
          header, footer, .no-print { display: none !important; }
          main { padding: 0 !important; margin: 0 !important; min-height: 0 !important; }
          .report-page { color: black !important; background: white !important; padding: 0 !important; max-width: 100% !important; }
          .report-page * { color: black !important; border-color: #999 !important; }
          .report-page .report-accent { color: #E3292E !important; }
          .report-page .report-accent-green { color: #239458 !important; }
          .report-page .report-bg { background: #f5f5f5 !important; }
        }
      `}</style>
      <div className="max-w-4xl mx-auto space-y-6 report-page" data-testid="match-report">
        <div className="flex justify-between items-center no-print">
          <Link to="/spielplan" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest" style={{ color: "var(--dc-muted)" }} data-testid="report-back">
            <ArrowLeft size={14} /> Zurück
          </Link>
          <button className="dc-btn" onClick={() => window.print()} data-testid="report-print-button">
            <Printer size={14} /> PDF / Drucken
          </button>
        </div>

        <div className="dc-border p-8 report-bg">
          <div className="text-center mb-6">
            {clubLogo && <img src={clubLogo} alt={`${getActiveClubName()} Logo`} className="w-20 h-20 object-contain dc-border p-1 mx-auto mb-3" style={{ background: "#fff" }} />}
            <div className="text-xs uppercase tracking-[0.4em] report-accent">Spielbericht · {getActiveClubName()}</div>
            <div className="font-display text-3xl mt-1">{season?.name}</div>
            <div className="text-sm mt-1" style={{ color: "var(--dc-muted)" }}>
              {match.round === 1 ? "Hinrunde" : "Rückrunde"} · Spieltag {match.matchday}
              {match.played_at && ` · ${new Date(match.played_at).toLocaleDateString("de-DE")}`}
            </div>
          </div>

          <div className="grid grid-cols-3 items-center gap-4 border-y dc-border py-6">
            <div className="text-center md:text-right">
              <div className="font-display text-4xl">{player1.name}</div>
              {player1.nickname && <div className="text-sm" style={{ color: "var(--dc-muted)" }}>&quot;{player1.nickname}&quot;</div>}
            </div>
            <div className="text-center">
              <div className="font-mono-data font-display text-7xl flex items-center justify-center gap-4">
                <span className={match.winner_id === player1.id ? "report-accent-green" : ""}>{match.legs_p1}</span>
                <span style={{ color: "var(--dc-muted)" }}>:</span>
                <span className={match.winner_id === player2.id ? "report-accent-green" : ""}>{match.legs_p2}</span>
              </div>
              <div className="text-xs uppercase tracking-widest mt-2" style={{ color: "var(--dc-muted)" }}>Best of 3</div>
            </div>
            <div className="text-center md:text-left">
              <div className="font-display text-4xl">{player2.name}</div>
              {player2.nickname && <div className="text-sm" style={{ color: "var(--dc-muted)" }}>&quot;{player2.nickname}&quot;</div>}
            </div>
          </div>

          {match.played && (
            <div className="mt-6 text-center">
              <div className="text-xs uppercase tracking-widest" style={{ color: "var(--dc-muted)" }}>Sieger</div>
              <div className="font-display text-2xl report-accent">{winner.name}</div>
            </div>
          )}
        </div>

        {match.played && (
          <>
            <div className="dc-border p-6">
              <h2 className="font-display text-2xl mb-4 flex items-center gap-2"><Target size={18} /> Leg-Details</h2>
              <table className="w-full font-mono-data text-sm">
                <thead style={{ color: "var(--dc-muted)" }} className="text-xs uppercase">
                  <tr>
                    <th className="text-left py-2">Leg</th>
                    <th className="text-left">Sieger</th>
                    <th className="text-right px-2">Finish</th>
                    <th className="text-center">Ausgebullt</th>
                    <th className="text-right px-2">Avg P1</th>
                    <th className="text-right px-2">Avg P2</th>
                    <th className="text-right px-2">180 P1</th>
                    <th className="text-right px-2">180 P2</th>
                  </tr>
                </thead>
                <tbody>
                  {match.legs.map((leg) => (
                    <tr key={`leg-${leg.leg_number}`} className="border-t dc-border" data-testid={`report-leg-${leg.leg_number}`}>
                      <td className="py-2">{leg.leg_number}</td>
                      <td style={{ fontFamily: "Manrope" }}>{leg.winner_id === player1.id ? player1.name : player2.name}</td>
                      <td className="text-right px-2">{leg.finish_score ?? "—"}</td>
                      <td className="text-center">{leg.ausgebullt ? "✓" : "—"}</td>
                      <td className="text-right px-2">{leg.avg_p1 ?? "—"}</td>
                      <td className="text-right px-2">{leg.avg_p2 ?? "—"}</td>
                      <td className="text-right px-2">{leg.s180_p1 || 0}</td>
                      <td className="text-right px-2">{leg.s180_p2 || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TotalsCard player={player1} totals={totals.p1} />
              <TotalsCard player={player2} totals={totals.p2} />
            </div>
          </>
        )}

        {!match.played && (
          <div className="dc-border p-10 text-center" style={{ color: "var(--dc-muted)" }}>
            Diese Partie wurde noch nicht gespielt.
          </div>
        )}

        <div className="text-xs text-center pt-4" style={{ color: "var(--dc-muted)" }}>
          Erstellt am {new Date().toLocaleDateString("de-DE")} · {getActiveClubName()}
        </div>
      </div>
    </>
  );
}

function TotalsCard({ player, totals }) {
  return (
    <div className="dc-border p-6" data-testid={`totals-${player.id}`}>
      <div className="font-display text-2xl mb-3">{player.name}</div>
      <div className="grid grid-cols-2 gap-3 font-mono-data text-sm">
        <div><span style={{ color: "var(--dc-muted)" }} className="text-xs uppercase tracking-widest block">Match Avg</span> <span className="text-2xl">{totals.avg || "—"}</span></div>
        <div><span style={{ color: "var(--dc-muted)" }} className="text-xs uppercase tracking-widest block">Finish-Ø</span> <span className="text-2xl">{totals.finish_average || "—"}</span></div>
        <div><span style={{ color: "var(--dc-muted)" }} className="text-xs uppercase tracking-widest block">180er</span> <span className="text-2xl">{totals.s180}</span></div>
        <div><span style={{ color: "var(--dc-muted)" }} className="text-xs uppercase tracking-widest block">High Finish</span> <span className="text-2xl">{totals.high_finish || "—"}</span></div>
        <div><span style={{ color: "var(--dc-muted)" }} className="text-xs uppercase tracking-widest block">Ausgebullt</span> <span className="text-2xl">{totals.ausgebullt}</span></div>
      </div>
    </div>
  );
}
